import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "../../lib/api-client";
import { useAuthStore } from "../../stores/authStore";

export interface Invite {
  token: string;
  createdBy: string;
  email?: string;
  status: "pending" | "used" | "expired" | "revoked";
  usedBy?: string;
  usedAt?: string;
  expiresAt: string;
  createdAt: string;
}

interface InviteVerifyResponse {
  valid: boolean;
  reason?: "already_used" | "expired" | "revoked";
  expiresAt?: string;
}

interface AcceptInviteResponse {
  user: {
    email: string;
    name: string;
    role: string;
    createdAt: string;
    updatedAt: string;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
  };
}

interface InvitesResponse {
  invites: Invite[];
}

interface RevokeInviteResponse {
  message: string;
}

export function useVerifyInvite(token: string | undefined) {
  return useQuery({
    queryKey: ["invite", token],
    queryFn: () => {
      if (!token) {
        throw new Error("Token is required");
      }
      return apiGet<InviteVerifyResponse>(`/invites/${token}`);
    },
    enabled: Boolean(token),
    retry: false,
  });
}

export function useAcceptInvite() {
  const login = useAuthStore((state) => state.login);

  return useMutation({
    mutationFn: (data: {
      token: string;
      email: string;
      name: string;
    }) => {
      return apiPost<AcceptInviteResponse>(`/invites/${data.token}/accept`, {
        email: data.email,
        name: data.name,
      });
    },
    onSuccess: (data) => {
      const isAdmin = data.user.role === "admin";
      login(
        {
          email: data.user.email,
          name: data.user.name,
          isAdmin,
        },
        data.tokens.accessToken,
        data.tokens.refreshToken
      );
    },
  });
}

export function useInvites() {
  return useQuery({
    queryKey: ["admin", "invites"],
    queryFn: async () => {
      const response = await apiGet<InvitesResponse>("/admin/invites");
      return response.invites;
    },
  });
}

export function useCreateInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data?: { email?: string; expiresAt?: string }) => {
      return apiPost<Invite>("/admin/invites", data ?? {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "invites"] });
    },
  });
}

export function useRevokeInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) => {
      return apiDelete<RevokeInviteResponse>(`/admin/invites/${token}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "invites"] });
    },
  });
}
